import * as React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { CmsAddressSearch, FormSpacer, InformationPopUp } from 'components';
import { useContentContext } from '@qbe/dfc-content';
import ContactPreferredMethod from 'pages/common/components/ContactPreferredMethod';
import { FormDataType } from 'contexts/FormProvider';

// import { StyledText, Label } from '../InsuredForm.styled';
// import get from 'lodash/fp/get';

import { phoneNumberSchema } from '../schema';
import { SEPARATOR, YesNoList } from 'utils/constants';

import { AddressAutocomplete } from 'pages/common/components/AddressAutocomplete';
import { isRelationshipNeedsPreferredContact } from '../../ConsentForm/schema';
import { ContactNumber, AddressManual, ToggleController } from 'controllers';

import { FormGroupLabel } from '@qbe/ads-core';
import { FormCard, Toggle } from '@qbe/ads-blocks-forms';
import { TextFieldController } from 'controllers';
import { useContentComplaintPartners } from 'utils/content';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContactAddressHandlers } from 'hooks/useContactAddressHandlers';
import { AddressType } from 'components/AddressSearch/addressTypes';

interface ContactDetailProps {
  form: UseFormReturn<FormDataType>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formRef: any;
  contactType: string;
  isFieldRequired: (fieldName: keyof FormDataType) => boolean;
  children?: React.ReactNode;
}

const ContactDetail = ({ form, contactType, children, isFieldRequired }: ContactDetailProps) => {
  const {
    watch,
    control,
    getValues,
    register,
    setValue,
    trigger,
    setError,
    clearErrors,
    reset,
    formState: { touchedFields },
  } = form;

  const { DcomPrtrQasMigrationToggle = true } = useFlags();

  const {
    handleRepPostalAddressChange,
    handlePhysicalAddressChange,
    getRepPostalAddressDefaults,
    getPhysicalAddressDefaults,
    isManualAddressRes,
    isManualAddressPhy,
  } = useContactAddressHandlers({ form, qasMigrationEnabled: DcomPrtrQasMigrationToggle });

  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });
  const addressType = watch('addressType') || '';
  const iContactPreferedMethod = watch('iContactPreferedMethod');
  const relationship = watch('relationship') || '';
  const showPostalAddress = addressType === 'no';

  const mPhoneNumber = getValues('mPhoneNumber');
  const hPhoneNumber = getValues('hPhoneNumber');
  const oPhoneNumber = getValues('oPhoneNumber');

  const values: object = watch();

  React.useEffect(() => {
    const haveOneValidNumber = [mPhoneNumber, hPhoneNumber, oPhoneNumber].some((phoneNumber) =>
      phoneNumberSchema(false).isValidSync(phoneNumber)
    );

    setValue('haveOneValidNumber', haveOneValidNumber);

    if (iContactPreferedMethod === 'phone' && !haveOneValidNumber) {
      setError('haveOneValidNumber', { type: 'required', message: 'must have one valid number' });
    } else clearErrors('haveOneValidNumber');
  }, [hPhoneNumber, mPhoneNumber, oPhoneNumber, iContactPreferedMethod, setValue, clearErrors, setError]);

  return (
    <>
      <FormCard variant="sectionTitle" title="Contact Details">
        {(contactType === 'no' || !isRelationshipNeedsPreferredContact(relationship)) && (
          <>
            <Controller
              name="iContactPreferedMethod"
              control={control}
              render={({ field }) => {
                return (
                  <ContactPreferredMethod
                    {...field}
                    title={getTextContent('insured.contactDetails.preferedMethod')}
                    titleSuffix={
                      (
                        <InformationPopUp
                          message={getTextContent('insured.contactDetails.preferedMethod.inofText')}
                          dataTrack="infoIconPreferredContact"
                        />
                      ) as any
                    }
                    onAfterChange={() => {
                      (['cEmailAddress', 'mPhoneNumber', 'hPhoneNumber', 'oPhoneNumber'] as const).forEach((field) => {
                        if ((getValues(field) || '').length > 0) {
                          trigger(field);
                        } else {
                          clearErrors(field);
                        }
                      });
                    }}
                  />
                );
              }}
            />
          </>
        )}
        <FormSpacer type="input" />
        <FormGroupLabel
          alignCenter
          legendElementOveride
          required={iContactPreferedMethod === 'email'}
          title={getTextContent('insured.contactDetails.emailAddress')}
        />
        <FormSpacer type="input" />
        <TextFieldController
          control={control}
          name="cEmailAddress"
          label="Email address"
          maxLength={100}
          inputProps={{
            maxLength: 100,
            ['data-track']: 'emailAddressCustomers',
          }}
        />

        <FormGroupLabel
          alignCenter
          legendElementOveride
          required
          title={getTextContent('insured.contactDetails.residentialAddress')}
        />
        <FormSpacer type="input" />
        {DcomPrtrQasMigrationToggle ? (
          <CmsAddressSearch
            type={AddressType.RepPostal}
            required={true}
            onAddressChange={handleRepPostalAddressChange}
            getAddressDefaults={getRepPostalAddressDefaults}
          />
        ) : (
          <>
            {!isManualAddressRes && (
              <AddressAutocomplete
                form={form}
                label="Residential Address"
                fieldName="residentialAddress"
                fieldNameIsValid="isResAddressValid"
                fieldNameIsManual="isManualAddressRes"
                dataTrack="residentialAddress"
                dataTrackValue="mask"
                linkType="residential"
                addressFieldNames={{
                  country: 'rCountry',
                  streetAddress: 'rStreetAddress',
                  suburb: 'rSuburb',
                  state: 'rState',
                  postCode: 'rPostcode',
                }}
              />
            )}
            {isManualAddressRes && <AddressManual form={form} prefix="r" />}
          </>
        )}

        <ToggleController
          control={control}
          name="addressType"
          options={YesNoList}
          required
          title={getTextContent('insured.contactDetails.addressType')}
          capture
          onAfterChange={(e) => {
            const { value } = e.target as any;
            if (value === 'yes') {
              setValue('cPostalAddress', null, { shouldValidate: true });
              setValue('cPostalAddressObject', null, { shouldValidate: true });
            }
          }}
        />
        {DcomPrtrQasMigrationToggle && showPostalAddress ? (
          <CmsAddressSearch
            type={AddressType.Physical}
            required={true}
            onAddressChange={handlePhysicalAddressChange}
            getAddressDefaults={getPhysicalAddressDefaults}
          />
        ) : (
          <>
            {showPostalAddress && !isManualAddressPhy && (
              <>
                <FormSpacer type="input" />

                <AddressAutocomplete
                  form={form}
                  label="Postal Address"
                  fieldName="cPostalAddress"
                  fieldNameIsValid="isPhyAddressValid"
                  fieldNameIsManual="isManualAddressPhy"
                  linkType="physical"
                  addressFieldNames={{
                    country: 'pCountry',
                    streetAddress: 'pStreetAddress',
                    suburb: 'pSuburb',
                    state: 'pState',
                    postCode: 'pPostcode',
                  }}
                  dataTrack="cPostalAddress"
                  dataTrackValue="mask"
                />
              </>
            )}

            {showPostalAddress && isManualAddressPhy && (
              <>
                <FormSpacer type="input" />
                <AddressManual prefix="p" form={form} />
              </>
            )}
          </>
        )}

        <FormGroupLabel
          alignCenter
          required={iContactPreferedMethod === 'phone'}
          title={getTextContent('insured.contactDetails.phoneNumber')}
        />
        <FormSpacer type="input" />

        <ContactNumber form={form as any} />

        {children}
      </FormCard>
    </>
  );
};

export default ContactDetail;
